import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Badge, Form, Row, Col, Button, Pagination, Spinner, Modal } from 'react-bootstrap';
import apiClient from '../../api';
import { toast } from 'react-toastify';

const BookingSearchLogsView = ({ bases = [] }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedLog, setSelectedLog] = useState(null);
    const limit = 50;

    const [filters, setFilters] = useState({
        start_date: new Date(Date.now() - 3 * 86400000).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        country: '',
        base_id: '',
        status: 'ALL',
        is_bot: 'ALL',
        search: ''
    });

    const fetchSearchLogs = useCallback(async (targetPage = 1) => {
        setLoading(true);
        try {
            const params = {
                page: targetPage,
                limit: limit,
                start_date: filters.start_date,
                end_date: filters.end_date,
                country: filters.country || undefined,
                base_id: filters.base_id || undefined,
                status: filters.status !== 'ALL' ? filters.status : undefined,
                is_bot: filters.is_bot === 'BOT' ? true : (filters.is_bot === 'NORMAL' ? false : undefined),
                search: filters.search || undefined
            };

            const res = await apiClient.get('/booking/analytics/search-logs', { params });
            setLogs(res.data.data || []);
            setTotal(res.data.total || 0);
            setPage(res.data.page || 1);
            setTotalPages(res.data.total_pages || 1);
        } catch (err) {
            console.error("Error al obtener logs de búsqueda Booking:", err);
            toast.error("Error al cargar las consultas de Booking.");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchSearchLogs(1);
    }, [fetchSearchLogs]);

    const handleFilterChange = (key, val) => {
        setFilters(prev => ({ ...prev, [key]: val }));
    };

    const handleExportExcel = async () => {
        try {
            toast.info("Exportando reporte CSV de búsquedas...");
            const params = new URLSearchParams({
                start_date: filters.start_date,
                end_date: filters.end_date,
                country: filters.country || ''
            });
            if (filters.base_id) params.append('base_id', filters.base_id);
            if (filters.status && filters.status !== 'ALL') params.append('status', filters.status);
            if (filters.is_bot !== 'ALL') params.append('is_bot', filters.is_bot);
            if (filters.search) params.append('search', filters.search);
            window.open(`${process.env.REACT_APP_API_URL || 'https://flujo-app-backend.onrender.com'}/booking/search-logs/export?${params.toString()}`, '_blank');
            toast.success("Reporte descargado.");
        } catch (e) {
            toast.error("Error al exportar.");
        }
    };

    const renderPriceSourceBadge = (item) => {
        if (!item.lowest_price && item.status !== 'AVAILABLE') {
            return <span className="text-muted fst-italic">Sin tarifa</span>;
        }

        const source = item.price_source;
        const provName = item.provider_name || (source && source.startsWith("Proveedor:") ? source.replace("Proveedor:", "").trim() : null);

        if (provName) {
            return (
                <Badge 
                    bg="success" 
                    className="px-2 py-1 shadow-sm d-inline-flex align-items-center gap-1"
                    title="Tarifa de Proveedor. Click para ver desglose"
                    onClick={() => setSelectedLog(item)}
                    style={{ cursor: 'pointer' }}
                >
                    <span>🤝</span> <b>{provName}</b>
                </Badge>
            );
        }

        if (source && source.startsWith("Por Km")) {
            return (
                <Badge 
                    bg="warning" 
                    text="dark" 
                    className="px-2 py-1 shadow-sm d-inline-flex align-items-center gap-1" 
                    title="Click para ver desglose de cotización"
                    onClick={() => setSelectedLog(item)}
                    style={{ cursor: 'pointer' }}
                >
                    <span>📏</span> <b>{source}</b>
                </Badge>
            );
        }

        if (source && (source.includes("Perú") || source.includes("Peru"))) {
            return (
                <Badge 
                    bg="info" 
                    className="px-2 py-1 shadow-sm text-dark d-inline-flex align-items-center gap-1" 
                    title="Click para ver desglose de cotización"
                    onClick={() => setSelectedLog(item)}
                    style={{ cursor: 'pointer' }}
                >
                    <span>🇵🇪</span> <b>{source}</b>
                </Badge>
            );
        }

        // Tarifa propia de la empresa
        return (
            <Badge 
                className="px-2 py-1 shadow-sm d-inline-flex align-items-center gap-1 text-white" 
                style={{ backgroundColor: '#6f42c1', cursor: 'pointer' }} 
                title="Tarifa directa EmiGo. Click para ver desglose"
                onClick={() => setSelectedLog(item)}
            >
                <span>🏢</span> <b>EmiGo</b>
            </Badge>
        );
    };

    return (
        <div className="animate-fade-in">
            {/* BARRA DE FILTROS AVANZADA (FOTO 1) */}
            <Card className="mb-4 shadow-sm border-0">
                <Card.Header className="bg-primary text-white fw-bold d-flex justify-content-between align-items-center py-2">
                    <span>🔍 Filtros de Búsqueda y Auditoría de Consultas Booking</span>
                    <Button variant="outline-light" size="sm" className="fw-bold" onClick={handleExportExcel}>
                        📊 Exportar a Excel (CSV)
                    </Button>
                </Card.Header>
                <Card.Body className="p-3 bg-light">
                    <Row className="g-2 align-items-end">
                        <Col xl={2} lg={3} md={4} sm={6}>
                            <Form.Label className="small fw-bold text-muted mb-1">Fecha Inicio</Form.Label>
                            <Form.Control 
                                type="date" 
                                size="sm" 
                                value={filters.start_date} 
                                onChange={e => handleFilterChange('start_date', e.target.value)} 
                            />
                        </Col>
                        <Col xl={2} lg={3} md={4} sm={6}>
                            <Form.Label className="small fw-bold text-muted mb-1">Fecha Fin</Form.Label>
                            <Form.Control 
                                type="date" 
                                size="sm" 
                                value={filters.end_date} 
                                onChange={e => handleFilterChange('end_date', e.target.value)} 
                            />
                        </Col>
                        <Col xl={2} lg={2} md={4} sm={6}>
                            <Form.Label className="small fw-bold text-muted mb-1">País</Form.Label>
                            <Form.Select 
                                size="sm" 
                                value={filters.country} 
                                onChange={e => handleFilterChange('country', e.target.value)}
                            >
                                <option value="">(Todos los Países)</option>
                                <option value="chile">Chile (SCL)</option>
                                <option value="peru">Perú (LIM)</option>
                                <option value="dominicana">Rep. Dominicana (PUJ)</option>
                                <option value="argentina">Argentina (EZE)</option>
                                <option value="brasil">Brasil (GRU / GIG)</option>
                            </Form.Select>
                        </Col>
                        <Col xl={2} lg={2} md={4} sm={6}>
                            <Form.Label className="small fw-bold text-muted mb-1">Base / Sub Sede</Form.Label>
                            <Form.Select 
                                size="sm" 
                                value={filters.base_id} 
                                onChange={e => handleFilterChange('base_id', e.target.value)}
                            >
                                <option value="">(Todas las Bases)</option>
                                {bases.map(b => (
                                    <option key={b.id} value={b.id}>{b.name}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col xl={2} lg={2} md={4} sm={6}>
                            <Form.Label className="small fw-bold text-muted mb-1">Resultado Cobertura</Form.Label>
                            <Form.Select 
                                size="sm" 
                                value={filters.status} 
                                onChange={e => handleFilterChange('status', e.target.value)}
                            >
                                <option value="ALL">Todos los Resultados</option>
                                <option value="AVAILABLE">✅ Disponibles (Con Precio)</option>
                                <option value="NO_AVAILABILITY">❌ Sin Cobertura / Sin Tarifa</option>
                            </Form.Select>
                        </Col>
                        <Col xl={2} lg={2} md={4} sm={6}>
                            <Form.Label className="small fw-bold text-muted mb-1">Filtro Detección BOT</Form.Label>
                            <Form.Select 
                                size="sm" 
                                value={filters.is_bot} 
                                onChange={e => handleFilterChange('is_bot', e.target.value)}
                            >
                                <option value="ALL">Todas las Consultas</option>
                                <option value="BOT">🤖 Solo Repetidos / BOT (+20%)</option>
                                <option value="NORMAL">👤 Solo Clientes Normales</option>
                            </Form.Select>
                        </Col>
                        <Col xl={10} lg={9} md={8} sm={12}>
                            <Form.Control 
                                type="text" 
                                size="sm" 
                                placeholder="🔍 Buscar por dirección de origen o destino..." 
                                value={filters.search} 
                                onChange={e => handleFilterChange('search', e.target.value)} 
                            />
                        </Col>
                        <Col xl={2} lg={3} md={4} sm={12}>
                            <Button variant="primary" size="sm" className="w-100 fw-bold" onClick={() => fetchSearchLogs(1)} disabled={loading}>
                                {loading ? <Spinner animation="border" size="sm" /> : "Consultar"}
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* TABLA PRINCIPAL DE BÚSQUEDAS */}
            <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                    <span className="fw-bold text-dark">
                        📋 Registro Fila por Fila de Consultas Recibidas desde Booking.com
                    </span>
                    <Badge bg="info" className="fs-6">
                        Total Encontrados: {total.toLocaleString()} consultas
                    </Badge>
                </Card.Header>
                <Card.Body className="p-0 table-responsive">
                    <Table hover striped className="mb-0 align-middle text-nowrap" style={{ fontSize: '12px' }}>
                        <thead className="bg-dark text-white fw-bold">
                            <tr>
                                <th># ID</th>
                                <th>Fecha / Hora Consulta</th>
                                <th>Origen</th>
                                <th>Destino</th>
                                <th>Km</th>
                                <th>Pax</th>
                                <th>Fecha Pickup (Viaje)</th>
                                <th>Estado Cobertura</th>
                                <th>Menor Precio Cotizado</th>
                                <th>Origen del Precio</th>
                                <th>Genius</th>
                                <th>Opciones</th>
                                <th>Detección BOT</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="13" className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-2 text-muted fw-bold">Cargando consultas de Booking...</p>
                                    </td>
                                </tr>
                            ) : logs.length === 0 ? (
                                <tr>
                                    <td colSpan="13" className="text-center py-4 text-muted">
                                        No se encontraron consultas de Booking con los filtros seleccionados.
                                    </td>
                                </tr>
                            ) : (
                                logs.map(item => (
                                    <tr key={item.id} className={item.is_bot ? "table-warning" : ""}>
                                        <td className="fw-bold text-muted">#{item.id}</td>
                                        <td>
                                            {item.timestamp ? new Date(item.timestamp).toLocaleString('es-CL') : '-'}
                                        </td>
                                        <td style={{ maxWidth: '220px' }} className="text-truncate" title={item.origin_address}>
                                            📍 {item.origin_address || 'Aeropuerto / Dirección'}
                                        </td>
                                        <td style={{ maxWidth: '220px' }} className="text-truncate" title={item.destination_address}>
                                            🏁 {item.destination_address || 'Hotel / Dirección'}
                                        </td>
                                        <td className="fw-bold">{item.distance_km ? `${item.distance_km} km` : '-'}</td>
                                        <td>👤 {item.passengers || 1}</td>
                                        <td>
                                            {item.pickup_time ? new Date(item.pickup_time).toLocaleString('es-CL') : '-'}
                                        </td>
                                        <td>
                                            {item.status === 'AVAILABLE' ? (
                                                <Badge bg="success" className="px-2 py-1">AVAILABLE</Badge>
                                            ) : item.status === 'RESTRICTED_LEAD_TIME' ? (
                                                <Badge bg="danger" className="px-2 py-1">RESTRINGIDA (Lead Time)</Badge>
                                            ) : (
                                                <Badge bg="secondary" className="px-2 py-1">NO AVAILABILITY</Badge>
                                            )}
                                        </td>
                                        <td className="fw-bold text-dark">
                                            {item.lowest_price ? `$${Math.round(item.lowest_price).toLocaleString('es-CL')} CLP` : '-'}
                                        </td>
                                        <td>
                                            {renderPriceSourceBadge(item)}
                                        </td>
                                        <td>
                                            {item.client_genius_level && item.client_genius_level !== 'NON_GENIUS' ? (
                                                <Badge 
                                                    bg="primary" 
                                                    style={{ backgroundColor: '#003580' }} 
                                                    className="px-2 py-1 shadow-sm d-inline-flex align-items-center gap-1"
                                                    title={item.is_genius_offered ? "Descuento Genius aplicado y ofrecido a Booking" : "Cliente Genius, pero sin descuento asignado en esta ruta"}
                                                >
                                                    <span>⭐</span> <b>{item.client_genius_level.replace('_', ' ')}</b>
                                                    {item.is_genius_offered ? (
                                                        <span className="badge bg-success ms-1" style={{ fontSize: '9px' }}>OFRECIDO</span>
                                                    ) : (
                                                        <span className="badge bg-secondary ms-1" style={{ fontSize: '9px' }}>0%</span>
                                                    )}
                                                </Badge>
                                            ) : (
                                                <span className="text-muted" style={{ fontSize: '11px' }}>-</span>
                                            )}
                                        </td>
                                        <td>
                                            <Badge bg="outline-dark" className="border text-dark">
                                                {item.returned_options_count || 0} categ.
                                            </Badge>
                                        </td>
                                        <td>
                                            {item.is_bot ? (
                                                <Badge bg="warning" text="dark" className="fw-bold px-2 py-1 shadow-sm">
                                                    🤖 BOT DETECTADO (+20%)
                                                </Badge>
                                            ) : (
                                                <Badge bg="light" text="dark" className="border px-2 py-1">
                                                    👤 Normal
                                                </Badge>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </Card.Body>

                {/* PAGINACIÓN */}
                {totalPages > 1 && (
                    <Card.Footer className="bg-white d-flex justify-content-between align-items-center py-2">
                        <small className="text-muted">
                            Mostrando página <b>{page}</b> de <b>{totalPages}</b> (Filas {((page - 1) * limit) + 1} - {Math.min(page * limit, total)})
                        </small>
                        <Pagination size="sm" className="mb-0">
                            <Pagination.First onClick={() => fetchSearchLogs(1)} disabled={page === 1 || loading} />
                            <Pagination.Prev onClick={() => fetchSearchLogs(page - 1)} disabled={page === 1 || loading} />
                            <Pagination.Item active>{page}</Pagination.Item>
                            <Pagination.Next onClick={() => fetchSearchLogs(page + 1)} disabled={page >= totalPages || loading} />
                            <Pagination.Last onClick={() => fetchSearchLogs(totalPages)} disabled={page >= totalPages || loading} />
                        </Pagination>
                    </Card.Footer>
                )}
            </Card>

            {/* MODAL DETALLE DE COTIZACIÓN Y ORIGEN DEL PRECIO */}
            <Modal show={!!selectedLog} onHide={() => setSelectedLog(null)} size="lg" centered>
                <Modal.Header closeButton className="bg-dark text-white">
                    <Modal.Title className="fs-6 fw-bold">
                        🔍 Detalle de Cotización Booking.com #{selectedLog?.id}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {selectedLog && (
                        <div>
                            {/* RUTA */}
                            <div className="p-3 bg-light rounded-3 mb-3 border">
                                <Row className="g-2">
                                    <Col md={6}>
                                        <small className="text-muted fw-bold d-block">📍 Origen Solicitado:</small>
                                        <div className="fw-bold text-dark">{selectedLog.origin_address}</div>
                                    </Col>
                                    <Col md={6}>
                                        <small className="text-muted fw-bold d-block">🏁 Destino Solicitado:</small>
                                        <div className="fw-bold text-dark">{selectedLog.destination_address}</div>
                                    </Col>
                                    <Col md={4} className="mt-2">
                                        <small className="text-muted fw-bold d-block">Distancia:</small>
                                        <span className="badge bg-secondary">{selectedLog.distance_km ? `${selectedLog.distance_km} km` : 'N/A'}</span>
                                    </Col>
                                    <Col md={4} className="mt-2">
                                        <small className="text-muted fw-bold d-block">Pasajeros:</small>
                                        <span>👤 {selectedLog.passengers || 1} pax</span>
                                    </Col>
                                    <Col md={4} className="mt-2">
                                        <small className="text-muted fw-bold d-block">Fecha / Hora del Servicio:</small>
                                        <span className="fw-bold text-primary">
                                            {selectedLog.pickup_time ? new Date(selectedLog.pickup_time).toLocaleString('es-CL') : '-'}
                                        </span>
                                    </Col>
                                    {selectedLog.client_genius_level && selectedLog.client_genius_level !== 'NON_GENIUS' && (
                                        <Col md={12} className="mt-2">
                                            <div className="p-2 rounded bg-light border d-flex align-items-center justify-content-between">
                                                <div>
                                                    <small className="text-muted fw-bold d-block">Programa Genius Booking:</small>
                                                    <span>⭐ Cliente <b>{selectedLog.client_genius_level.replace('_', ' ')}</b></span>
                                                </div>
                                                {selectedLog.is_genius_offered ? (
                                                    <Badge bg="success" className="px-2 py-1 fs-6">✅ Descuento Genius Ofrecido</Badge>
                                                ) : (
                                                    <Badge bg="secondary" className="px-2 py-1">Inactivo en esta ruta (0%)</Badge>
                                                )}
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            </div>

                            {/* ORIGEN DEL PRECIO */}
                            <Card className="border-primary border-opacity-25 mb-3 shadow-sm">
                                <Card.Header className="bg-primary bg-opacity-10 fw-bold text-primary py-2">
                                    💰 Origen y Procedencia de la Tarifa
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <Row className="g-3 align-items-center">
                                        <Col md={6}>
                                            <div className="small text-muted fw-bold">Mecanismo de Cotización:</div>
                                            <div className="fs-5 mt-1">{renderPriceSourceBadge(selectedLog)}</div>
                                            {selectedLog.provider_name && (
                                                <div className="text-success small fw-bold mt-2">
                                                    🤝 Proveedor: {selectedLog.provider_name}
                                                </div>
                                            )}
                                        </Col>
                                        <Col md={6} className="border-start ps-3">
                                            <div className="small text-muted fw-bold">Menor Venta Cotizada a Booking:</div>
                                            <div className="fs-4 fw-bold text-success">
                                                {selectedLog.lowest_price ? `$${Math.round(selectedLog.lowest_price).toLocaleString('es-CL')} CLP` : '-'}
                                            </div>
                                            {selectedLog.calculation_details?.cost_clp && (
                                                <div className="small text-muted mt-1">
                                                    Costo Chofer/Proveedor: <b>${Math.round(selectedLog.calculation_details.cost_clp).toLocaleString('es-CL')} CLP</b>
                                                    {selectedLog.calculation_details.currency !== 'CLP' && selectedLog.calculation_details.cost_local && (
                                                        <span> ({selectedLog.calculation_details.cost_local} {selectedLog.calculation_details.currency})</span>
                                                    )}
                                                </div>
                                            )}
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* OPCIONES POR CATEGORÍA OFRECIDAS */}
                            {selectedLog.calculation_details?.options_summary && selectedLog.calculation_details.options_summary.length > 0 && (
                                <div className="mt-3">
                                    <h6 className="fw-bold text-muted small mb-2">📋 Categorías y Opciones Cotizadas a Booking:</h6>
                                    <Table size="sm" bordered hover className="align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Categoría Booking</th>
                                                <th>Precio Venta</th>
                                                <th>Proveedor Asociado</th>
                                                <th>Tipo Regla</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedLog.calculation_details.options_summary.map((opt, idx) => (
                                                <tr key={idx}>
                                                    <td className="fw-bold">{opt.category}</td>
                                                    <td className="text-success fw-bold">${Math.round(opt.price).toLocaleString('es-CL')} CLP</td>
                                                    <td>{opt.provider || <span className="text-muted">Tarifa Base</span>}</td>
                                                    <td>
                                                        <Badge bg="light" text="dark" className="border">
                                                            {opt.source === 'distance_tiered' ? 'Por Km' : 'Matriz'}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="py-2">
                    <Button variant="secondary" size="sm" onClick={() => setSelectedLog(null)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default BookingSearchLogsView;
